import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchActiveSchools } from '../ApiHandler/schoolFunctions';
import { fetchActiveLabs } from '../ApiHandler/labFunctions';
import ViewStudents from './ViewStudents';

const StudentRegistration = ({ role }) => {
    const initialFormState = {
        lab_id: '',
        kendra_id: '',
        student_name: '',
        father_name: '',
        dob: '',
        email: '',
        gender: '',
        social_group: '',
        mobile_number: '',
        whatsapp_number: '',
        country: 'India',
        state: '',
        district: '',
        city_village: '',
        org_type: '',
        school_type: '',
        school_name: '',
        school_id: '',
        student_class: '',
        course_opted: '',
        how_to_know: [],
        agree_terms: false
    };

    const [formData, setFormData] = useState(initialFormState);
    const [schools, setSchools] = useState([]);
    const [labs, setLabs] = useState([]);
    const [kendras, setKendras] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [totalRegistered, setTotalRegistered] = useState(0);

    // Fetch Kendras when Lab changes
    useEffect(() => {
        if (formData.lab_id) {
            import('../ApiHandler/kendraFunctions').then(({ fetchKendras }) => {
                fetchKendras(formData.lab_id, setKendras);
            });
        } else {
            setKendras([]);
        }
    }, [formData.lab_id]);

    const states = [
        "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana",
        "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
        "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
        "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh",
        "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
    ];

    const sources = [
        "Advertisement", "School", "Friends", "Internet", "College", "Library", "Social Media", "Other"
    ];

    useEffect(() => {
        fetchActiveSchools(setSchools);
        fetchActiveLabs(setLabs);
        fetchTotalStudents();

        const editData = localStorage.getItem('satheeStudentToEdit') || localStorage.getItem('studentToEdit');
        if (editData) {
            const student = JSON.parse(editData);
            let parsedHowToKnow = [];
            try {
                parsedHowToKnow = student.how_to_know ? JSON.parse(student.how_to_know) : [];
            } catch (e) {
                parsedHowToKnow = student.how_to_know ? student.how_to_know.split(',') : [];
            }
            
            setFormData({
                lab_id: student.lab_id || '',
                kendra_id: student.kendra_id || '',
                student_name: student.student_name || `${student.first_name || ''} ${student.last_name || ''}`.trim(),
                father_name: student.father_name || '',
                dob: student.dob ? student.dob.split('T')[0] : '',
                email: student.email || '',
                gender: student.gender || '',
                social_group: student.social_group || '',
                mobile_number: student.mobile_number || '',
                whatsapp_number: student.whatsapp_number || '',
                country: student.country || 'India',
                state: student.state || '',
                district: student.district || '',
                city_village: student.city_village || '',
                org_type: student.org_type || '',
                school_type: student.school_type || '',
                school_name: student.school_name || '',
                school_id: student.school_id || (student.school_name ? 'other' : ''),
                student_class: student.student_class || '',
                course_opted: student.course_opted || '',
                how_to_know: Array.isArray(parsedHowToKnow) ? parsedHowToKnow : [],
                agree_terms: true
            });
            setIsEditing(true);
            setEditId(student.id);
            localStorage.removeItem('satheeStudentToEdit');
            localStorage.removeItem('studentToEdit');
        }
    }, []);

    const fetchTotalStudents = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await Axios.get(`${process.env.REACT_APP_API_URL}/students/fetchStudents`, {
                headers: { Authorization: token }
            });
            if(res.data && res.data.data) {
                setTotalRegistered(res.data.data.length);
            }
        } catch(err) {
            console.error(err);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (type === 'checkbox') {
            if (name === 'agree_terms') {
                setFormData({ ...formData, agree_terms: checked });
            } else if (name === 'how_to_know') {
                const current = [...formData.how_to_know];
                if (checked) current.push(value);
                else current.splice(current.indexOf(value), 1);
                setFormData({ ...formData, how_to_know: current });
            }
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleAddStudent = async (e) => {
        e.preventDefault();
        if (!formData.agree_terms) {
            toast.error('You must agree to the terms and conditions');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const url = isEditing 
                ? `${process.env.REACT_APP_API_URL}/students/update/${editId}`
                : `${process.env.REACT_APP_API_URL}/students/addStudent`;
            
            const method = isEditing ? 'put' : 'post';
            const payload = { 
                ...formData, 
                how_to_know: JSON.stringify(formData.how_to_know),
                school_id: formData.school_id === 'other' ? null : formData.school_id,
                school_name: formData.school_id === 'other' ? formData.school_name : null 
            };

            const response = await Axios[method](url, payload, {
                headers: { Authorization: token }
            });

            if (response.data.status === 'success') {
                toast.success(response.data.message);
                if (!isEditing) {
                    setFormData(initialFormState);
                    fetchTotalStudents();
                } else {
                    setTimeout(() => window.location.reload(), 1500);
                }
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Error saving sathee student:', error);
            toast.error('Failed to save student!');
        }
    };

    return (
        <main style={{ padding: '20px', backgroundColor: '#f9f9fc', minHeight: '100vh' }}>
            <ToastContainer />
            <div className="head-title">
                <div className="left">
                    <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#333' }}>
                        <i className='bx bxs-graduation' style={{ color: '#6C5CE7' }}></i> 
                        Student Registration
                    </h1>
                    <p style={{ color: '#888', marginTop: '5px' }}>Register students for Upslate Labs exam preparation</p>
                </div>
            </div>

            <div style={{ backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginTop: '20px', overflow: 'hidden' }}>
                <div style={{ backgroundColor: '#6C5CE7', color: 'white', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
                        <i className='bx bx-user-plus'></i> {isEditing ? 'Edit Student Registration' : 'New Student Registration'}
                    </h3>
                    <span style={{ backgroundColor: 'rgba(255,255,255,0.2)', padding: '5px 15px', borderRadius: '20px', fontSize: '13px' }}>
                        {totalRegistered} Registered
                    </span>
                </div>
                
                <form onSubmit={handleAddStudent} style={{ padding: '30px' }}>
                    
                    {/* PERSONAL INFORMATION */}
                    <h4 style={sectionHeaderStyle}><i className='bx bx-user' style={{marginRight:'8px'}}></i> PERSONAL INFORMATION</h4>
                    
                    <div className='responsive-grid'>
                        <div className="form-group">
                            <label style={labelStyle}>Lab <span style={reqStyle}>*</span></label>
                            <select name="lab_id" value={formData.lab_id} onChange={handleInputChange} required style={inputStyle}>
                                <option value="" disabled>Select Lab</option>
                                {labs.map(lab => <option key={lab.id} value={lab.id}>{lab.lab_name}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label style={labelStyle}>Name <span style={reqStyle}>*</span></label>
                            <input type="text" name="student_name" value={formData.student_name} onChange={handleInputChange} placeholder="Enter student name" required style={inputStyle} />
                        </div>
                        <div className="form-group">
                            <label style={labelStyle}>Father's Name <span style={reqStyle}>*</span></label>
                            <input type="text" name="father_name" value={formData.father_name} onChange={handleInputChange} placeholder="Enter father's name" required style={inputStyle} />
                        </div>
                    </div>
                    
                    <div className='responsive-grid'>
                        <div className="form-group">
                            <label style={labelStyle}>Date of Birth <span style={reqStyle}>*</span></label>
                            <input type="date" name="dob" value={formData.dob} onChange={handleInputChange} required style={inputStyle} />
                        </div>
                        <div className="form-group">
                            <label style={labelStyle}>Email Id <span style={reqStyle}>*</span></label>
                            <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Enter student email address" required style={inputStyle} />
                        </div>
                        <div className="form-group">
                            <label style={labelStyle}>Gender <span style={reqStyle}>*</span></label>
                            <select name="gender" value={formData.gender} onChange={handleInputChange} required style={inputStyle}>
                                <option value="" disabled>Select Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    <div className='responsive-grid-2'>
                        <div className="form-group">
                            <label style={labelStyle}>Social Group <span style={reqStyle}>*</span></label>
                            <select name="social_group" value={formData.social_group} onChange={handleInputChange} required style={inputStyle}>
                                <option value="" disabled>Select Social Group</option>
                                <option value="General">General</option>
                                <option value="OBC">OBC</option>
                                <option value="SC">SC</option>
                                <option value="ST">ST</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label style={labelStyle}>Mobile Number</label>
                            <input type="text" name="mobile_number" value={formData.mobile_number} onChange={handleInputChange} placeholder="Preferably WhatsApp" style={inputStyle} />
                            <small style={{color: '#999', fontSize: '11px'}}>Preferably WhatsApp — optional</small>
                        </div>
                    </div>

                    <hr style={dividerStyle} />

                    {/* LOCATION */}
                    <h4 style={sectionHeaderStyle}><i className='bx bx-map' style={{marginRight:'8px'}}></i> LOCATION & KENDRA ASSIGNMENT</h4>
                    <div className='responsive-grid'>
                        <div className="form-group">
                            <label style={labelStyle}>Country <span style={reqStyle}>*</span></label>
                            <input type="text" name="country" value={formData.country} readOnly style={{...inputStyle, backgroundColor: '#f5f5f5'}} />
                        </div>
                        <div className="form-group">
                            <label style={labelStyle}>State <span style={reqStyle}>*</span></label>
                            <select name="state" value={formData.state} onChange={handleInputChange} required style={inputStyle}>
                                <option value="" disabled>Select State</option>
                                {states.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label style={labelStyle}>District <span style={reqStyle}>*</span></label>
                            <input type="text" name="district" value={formData.district} onChange={handleInputChange} placeholder="Enter district" required style={inputStyle} />
                        </div>
                    </div>
                    <div className='responsive-grid-1'>
                        <div className="form-group">
                            <label style={labelStyle}>City / Village <span style={reqStyle}>*</span></label>
                            <input type="text" name="city_village" value={formData.city_village} onChange={handleInputChange} placeholder="Enter city or village name" required style={inputStyle} />
                        </div>
                        {kendras.length > 0 && (
                            <div className="form-group" style={{marginTop: '20px'}}>
                                <label style={labelStyle}>Assign to Kendra</label>
                                <select name="kendra_id" value={formData.kendra_id} onChange={handleInputChange} style={inputStyle}>
                                    <option value="">-- No Kendra Assigned --</option>
                                    {kendras.map(k => <option key={k.id} value={k.id}>{k.kendra_name}</option>)}
                                </select>
                            </div>
                        )}
                    </div>

                    <hr style={dividerStyle} />

                    {/* SCHOOL & COURSE */}
                    <h4 style={sectionHeaderStyle}><i className='bx bx-building-house' style={{marginRight:'8px'}}></i> SCHOOL & COURSE</h4>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginBottom: '20px'}}>
                        <div className="form-group">
                            <label style={labelStyle}>Organization Type <span style={reqStyle}>*</span></label>
                            <select name="org_type" value={formData.org_type} onChange={handleInputChange} required style={inputStyle}>
                                <option value="School">School</option>
                                <option value="College">College</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px'}}>
                        <div className="form-group">
                            <label style={labelStyle}>School Type <span style={reqStyle}>*</span></label>
                            <select name="school_type" value={formData.school_type} onChange={handleInputChange} required style={inputStyle}>
                                <option value="" disabled>Select School Type</option>
                                <option value="Government">Government</option>
                                <option value="Private">Private</option>
                                <option value="Aided">Aided</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label style={labelStyle}>Class <span style={reqStyle}>*</span></label>
                            <select name="student_class" value={formData.student_class} onChange={handleInputChange} required style={inputStyle}>
                                <option value="" disabled>Select Class</option>
                                <option value="9">Class 9</option>
                                <option value="10">Class 10</option>
                                <option value="11">Class 11</option>
                                <option value="12">Class 12</option>
                                <option value="Passed">12th Passed</option>
                            </select>
                        </div>
                    </div>
                    
                    <div style={{marginBottom: '5px'}}>
                        <small style={{color: '#999', fontSize: '12px', fontStyle: 'italic'}}>* Select 'Others' if your school name is not listed below</small>
                    </div>
                    
                    <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginBottom: '20px'}}>
                        <div className="form-group">
                            <label style={labelStyle}>School Name <span style={reqStyle}>*</span></label>
                            <select name="school_id" value={formData.school_id} onChange={handleInputChange} required={!formData.school_name} style={{...inputStyle, marginBottom: formData.school_id === 'other' ? '10px' : '0'}}>
                                <option value="" disabled>Select from list</option>
                                {schools.map(school => (
                                    <option key={school.id} value={school.id}>{school.school_name}</option>
                                ))}
                                <option value="other">Others (Manually enter school)</option>
                            </select>
                            
                            {formData.school_id === 'other' && (
                                <input type="text" name="school_name" value={formData.school_name} onChange={handleInputChange} placeholder="Enter school name" required style={inputStyle} />
                            )}
                        </div>
                    </div>

                    <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '20px'}}>
                        <div className="form-group">
                            <label style={labelStyle}>Preparing For Exam <span style={reqStyle}>*</span></label>
                            <select name="course_opted" value={formData.course_opted} onChange={handleInputChange} required style={inputStyle}>
                                <option value="" disabled>Select Exam</option>
                                <option value="JEE">JEE</option>
                                <option value="NEET">NEET</option>
                                <option value="CUET">CUET</option>
                                <option value="Board Exam">Board Exam</option>
                                <option value="SSC">SSC</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    <hr style={dividerStyle} />

                    {/* HOW DID YOU COME TO KNOW ABOUT Upslate Labs */}
                    <h4 style={{...sectionHeaderStyle, color: '#333', fontSize: '14px'}}><span style={{color: '#6C5CE7', fontWeight: 'bold', marginRight: '5px'}}>?</span> HOW DID YOU COME TO KNOW ABOUT Upslate Labs?</h4>
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px'}}>
                        {sources.map(source => (
                            <label key={source} style={{display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', border: '1px solid #eee', borderRadius: '6px', cursor: 'pointer'}}>
                                <input type="checkbox" name="how_to_know" value={source} checked={formData.how_to_know.includes(source)} onChange={handleInputChange} style={{width: '16px', height: '16px'}} />
                                <span style={{fontSize: '14px', color: '#555'}}>{source}</span>
                            </label>
                        ))}
                    </div>

                    <div style={{backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef', marginBottom: '30px'}}>
                        <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
                            <input type="checkbox" name="agree_terms" checked={formData.agree_terms} onChange={handleInputChange} style={{width: '18px', height: '18px'}} />
                            <span style={{fontSize: '14px', color: '#444'}}>I Agree to <a href="# " style={{color: '#6C5CE7', textDecoration: 'underline'}}>terms and conditions</a> of Upslate Labs student registration</span>
                        </label>
                    </div>

                    <div style={{display: 'flex', gap: '15px'}}>
                        <button type="submit" style={submitBtnStyle}>
                            <i className='bx bx-check'></i> {isEditing ? 'Update' : 'Submit'}
                        </button>
                        <button type="button" onClick={() => setFormData(initialFormState)} style={clearBtnStyle}>
                            <i className='bx bx-reset'></i> Clear
                        </button>
                    </div>

                </form>
            </div>

            {/* Display list of registered students */}
            <div style={{ marginTop: '30px' }}>
                <ViewStudents role={role} showAllEntities={true} />
            </div>
        </main>
    );
};

// Styles to match the screenshot precisely
const sectionHeaderStyle = {
    color: '#6C5CE7',
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '0.5px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center'
};

const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px',
    marginBottom: '20px'
};

const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#444',
    marginBottom: '8px'
};

const inputStyle = {
    width: '100%',
    padding: '12px 15px',
    borderRadius: '8px',
    border: '1px solid #dde1e5',
    fontSize: '14px',
    color: '#333',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
};

const reqStyle = {
    color: '#e74c3c'
};

const dividerStyle = {
    border: 'none',
    borderTop: '1px dashed #ddd',
    margin: '30px 0'
};

const submitBtnStyle = {
    padding: '12px 30px',
    backgroundColor: '#6C5CE7',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: '1',
    justifyContent: 'center'
};

const clearBtnStyle = {
    padding: '12px 30px',
    backgroundColor: '#fff',
    color: '#666',
    border: '1px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0
};

export default StudentRegistration;
